// SPDX-License-Identifier: GPL-3.0-or-later
// The seeded perk draft of a run. Pure: no I/O, no clock, no random source of its own.
// The caller passes the random source. The draw is a partial Fisher-Yates shuffle.

import { canTake, PERKS } from "./perks.ts";
import type { DraftChoices, PerkId, PerkStacks } from "./types.ts";

/** The number of cards in one draft. */
export const DRAFT_SIZE = 3;

/** Featured turn: the draft picks card 0 after this much hold time. Free play never auto-picks. */
export const AUTO_PICK_MS = 15_000;

/**
 * Map a random value to an offset in [0, span - 1]. A NaN or negative value gives 0.
 * A value of 1 or more gives span - 1.
 */
function offsetOf(draw: number, span: number): number {
  if (!(draw > 0)) return 0;
  return Math.min(span - 1, Math.floor(draw * span));
}

/**
 * Fill `out` with up to `DRAFT_SIZE` distinct perks that the run can still take.
 * Each eligible perk has the same chance to appear. Slots with no perk get `null`.
 * Returns the number of perks drawn.
 */
export function drawDraft(rng: { next(): number }, stacks: PerkStacks, out: DraftChoices): number {
  const pool: PerkId[] = [];
  for (const perk of PERKS) {
    if (canTake(stacks, perk.id)) pool.push(perk.id);
  }
  const count = Math.min(DRAFT_SIZE, pool.length);
  for (let i = 0; i < count; i++) {
    const j = i + offsetOf(rng.next(), pool.length - i);
    const picked = pool[j];
    pool[j] = pool[i] as PerkId;
    pool[i] = picked as PerkId;
    out[i] = picked as PerkId;
  }
  for (let i = count; i < DRAFT_SIZE; i++) out[i] = null;
  return count;
}
